import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, Menu, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material";
import Scrollbar from "../containers/Anatomy/Scrollbar";
import moment from "moment";
import DateSlider from "../components/Date";
import SearchModal from "../containers/Anatomy/SearchBox";
import ModalComponent from "../containers/Anatomy/Modal";
import { store } from "../redux/store";
import * as queueActions from '../redux/actions/queueActions'
import { useDispatch, useSelector } from "react-redux";
import * as slotActions from "../redux/actions/slotsActions";
import { useNavigate } from "react-router-dom";
import { useToast } from "../context/ToastContext";
import * as consultationActions from "../redux/actions/consultationActions";
import { formatWeeks } from "../utils/formatWeekAndDays";
import { useHospitalContext } from "../context/HospitalContext";
import usePermissions from "../utils/permissions";
import { useDoctor } from "../context/DoctorSelectionContext";
import { API } from "../api/api";
import { END_POINTS } from "../api/endpoints";
import OtpInput from "../containers/Auth/OtpInputs";
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ReactComponent as EditIcon } from '../assets/EditIcon.svg';
import { screenBasisPermission } from "../utils/permission";
import { trackEvent } from "../utils/firebase";
import { getStableIdempotencyKey } from "../utils/billing";
import { ReactComponent as Crown } from '../assets/crown.svg';
import { canJoinVideoCall } from "../utils/videoCall";

const offlineMethodLabels = {
    CASH: 'Cash',
    DIRECT_UPI: 'Direct UPI',
    CARD_TERMINAL: 'Card terminal',
};

const formatBillingAmount = (amountMinor, currency = 'INR') => {
    try {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency,
            maximumFractionDigits: 2,
        }).format(amountMinor / 100);
    } catch {
        return `${currency} ${(amountMinor / 100).toFixed(2)}`;
    }
};

const DashBoard = () => {
    const navigate = useNavigate();
    const showToast = useToast()
    const hasNavigated = useRef(false);
    const storedRoute = localStorage.getItem('screen');
    const containerRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const dispatch = useDispatch();
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [openBookAppointment, setShowDropdown] = useState(false);
    const [patientReffered, setPatientReffered] = useState({});
    const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
    const { selectedHospital, } = useHospitalContext();
    const { hasPermission } = usePermissions();
    const { selectedDoctor } = useDoctor()
    const [otpModalOpen, setOtpModalOpen] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [otp, setOtp] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [resendTimer, setResendTimer] = useState(30);
    const [canResend, setCanResend] = useState(false);
    const [isEditingPhone, setIsEditingPhone] = useState(false);
    const [updatedPhone, setUpdatedPhone] = useState(phoneNumber);
    const [menuAnchorPosition, setMenuAnchorPosition] = useState(null);
    const [actionMenuContext, setActionMenuContext] = useState(null);
    const [isMutatingQueue, setIsMutatingQueue] = useState(false);
    const pendingNavigationState = useRef(null);
    const [billingItems, setBillingItems] = useState([]);
    const [selectedBill, setSelectedBill] = useState(null);
    const offlineKeyRef = useRef(null);
    const [offlineMethod, setOfflineMethod] = useState("");
    const [isRecordingPayment, setIsRecordingPayment] = useState(false);

    const canViewBilling = hasPermission('BILL_VIEW');
    const canRecordOfflinePayment = hasPermission('OFFLINE_PAYMENT_CREATE');

    const loadBillingItems = useCallback(async (isCurrent = () => true) => {
        if (!selectedHospital?.id || !selectedDoctor?.userTenantId || !canViewBilling) {
            setBillingItems([]);
            return;
        }

        try {
            const pageSize = 100;
            const items = [];
            let page = 1;

            while (true) {
                const response = await API.get(
                    END_POINTS.billingCollectionQueue(
                        "",
                        page,
                        pageSize,
                        "outstanding",
                        selectedDate,
                        selectedDoctor.userTenantId,
                    ),
                );
                // Keep the last good queue on a failed poll instead of hiding Collect.
                if (!response?.success) return;

                const pageItems = Array.isArray(response.data) ? response.data : [];
                items.push(...pageItems);
                const total = Number(response.meta?.total);
                if (
                    !Number.isFinite(total) ||
                    items.length >= total ||
                    pageItems.length < pageSize
                ) {
                    break;
                }
                page += 1;
            }

            // Ignore responses for a previous date/doctor selection.
            if (isCurrent()) setBillingItems(items);
        } catch {
            // Transient failure: keep showing the last loaded queue.
        }
    }, [selectedDate, selectedDoctor?.userTenantId, selectedHospital?.id, canViewBilling]);

    useEffect(() => {
        if (!selectedHospital?.id) return;
        if (!selectedDoctor?.userTenantId) return;
        dispatch(slotActions.getSlotsRequestAction(selectedDate));
        dispatch(queueActions.getQueueRequestAction(selectedDate));
        const fetchQueueAndSlots = () => {
            dispatch(slotActions.getSlotsAfterSomeTimeRequestAction(selectedDate));
            dispatch(queueActions.getSilentQueueRequestAction(selectedDate));
        };
        setIsFirstLoad(false);
        fetchQueueAndSlots();

        const intervalId = setInterval(fetchQueueAndSlots, 10000);
        return () => clearInterval(intervalId);
    }, [dispatch, selectedDate, selectedHospital?.id, selectedDoctor?.userTenantId]);

    useEffect(() => {
        let active = true;
        const poll = () => loadBillingItems(() => active);
        poll();
        const intervalId = setInterval(poll, 10000);
        return () => {
            active = false;
            clearInterval(intervalId);
        };
    }, [loadBillingItems]);

    const queueReducer = useSelector((state) => state.queueReducer)
    const slotReducer = useSelector((state) => state.slotReducer)
    const { loading, hasNext, page } = slotReducer;
    const consultationReducer = useSelector((state) => state.consultationReducer)

    const [activeSlots, setActiveSlots] = useState([])

    const [activeQueues, setActiveQueues] = useState([])

    useEffect(() => {
        setActiveSlots([]);
        setActiveQueues([]);
    }, [selectedHospital?.id]);

    useEffect(() => {
        if (!otpModalOpen || !otpSent) return;

        if (resendTimer > 0) {
            const timer = setTimeout(() => {
                setResendTimer(prev => prev - 1);
            }, 1000);

            return () => clearTimeout(timer);
        }

        setCanResend(true);
    }, [resendTimer, otpModalOpen, otpSent]);

    useEffect(() => {
        if (queueReducer?.data && queueReducer.data !== activeQueues) {
            setActiveQueues(queueReducer.data);
        }
    }, [queueReducer?.data]);

    useEffect(() => {
        if (slotReducer?.data && slotReducer.data !== activeSlots) {
            setActiveSlots(slotReducer.data);
        }
    }, [slotReducer?.data]);


    useEffect(() => {
        const incoming = slotReducer?.silentSlot;
        if (!incoming || incoming.length === 0) return;

        setActiveSlots((prev) => {
            if (!prev || prev.length === 0) return incoming;

            const prevById = new Map(prev.map((s) => [s.id, s]));
            const merged = incoming.map((newSlot) => {
                const oldSlot = prevById.get(newSlot.id);
                if (!oldSlot) return newSlot;

                if (JSON.stringify(oldSlot) === JSON.stringify(newSlot)) {
                    return oldSlot;
                }
                return newSlot;
            });

            return merged;
        });
    }, [slotReducer?.silentSlot]);

    useEffect(() => {
        const incoming = queueReducer?.silentQueue;
        if (!incoming || incoming.length === 0) return;
    
        setActiveQueues((prev) => {
            if (!prev || prev.length === 0) return incoming;
    
            const prevById = new Map(prev.map((q) => [q.id, q]));
    
            return incoming.map((newQueue) => {
                const oldQueue = prevById.get(newQueue.id);
    
                if (!oldQueue) return newQueue;
    
                return JSON.stringify(oldQueue) === JSON.stringify(newQueue)
                    ? oldQueue
                    : newQueue;
            });
        });
    }, [queueReducer?.silentQueue]);

    useEffect(() => {
        const consultation = consultationReducer?.consultationCreated;

        if (!consultation?.id || hasNavigated.current) return;

        hasNavigated.current = true;


        let redirectPath;

        if (hasPermission('HISTORY_VIEW')) {
            redirectPath = `/consultation/${consultation.id}/patient/${consultation.patientId}/A/history?mode=edit`;
        } else if (hasPermission('VITALS_VIEW')) {
            redirectPath = `/consultation/${consultation.id}/patient/${consultation.patientId}/A/vitals?mode=edit`;
        } else if (hasPermission('OBSERVATIONS_VIEW')) {
            redirectPath = `/consultation/${consultation.id}/patient/${consultation.patientId}/A/observation?mode=edit`;
        } else if (hasPermission('TEST_VIEW')) {
            redirectPath = `/consultation/${consultation.id}/patient/${consultation.patientId}/A/antenatal?mode=edit`;
        } else if (hasPermission('TREATMENT_VIEW')) {
            redirectPath = `/consultation/${consultation.id}/patient/${consultation.patientId}/A/treatment?mode=edit`;
        }


        if (redirectPath) {
            navigate(redirectPath, { state: pendingNavigationState.current });
            pendingNavigationState.current = null;
        }

        store.dispatch(consultationActions.resetConsultationState());
    }, [consultationReducer?.consultationCreated, navigate]);

    useEffect(() => {
        if (consultationReducer?.status !== 'Consulation creation failure') return;
        showToast(
            consultationReducer?.error || 'Unable to start consultation',
            consultationReducer?.error?.includes('check in') ? 'warning' : 'error',
        );
        store.dispatch(consultationActions.resetConsultationState());
    }, [consultationReducer?.status, consultationReducer?.error, showToast]);

    const isFutureDate = moment(selectedDate, 'YYYY-MM-DD').isAfter(moment(), 'day');

    const getStartButtonTooltip = (appointment) => {
        if (isFutureDate) return 'Cannot start future appointment';
        if (!appointment?.patientIsVerified) return 'Verify patient first';
        if (canCheckInAppointment(appointment)) return 'Check in patient to add to queue';
        if (canStartConsultation(appointment)) return 'Start consultation';
        if (canResumeConsultation(appointment)) return 'Resume consultation';
        return 'Check in patient first';
    };

    const renderCheckedInChip = (appointment) => {
        if (!appointment?.checkedInAt) return null;
        return (
            <Chip
                label="In"
                size="small"
                sx={{
                    height: '16px',
                    fontSize: '8px',
                    backgroundColor: '#C8E6C9',
                }}
            />
        );
    };

    const billingByAppointmentId = Object.fromEntries(
        billingItems.map((item) => [item.appointmentId, item]),
    );

    const getBillingItem = (appointment) =>
        billingByAppointmentId[appointment?.id] || null;

    const getPaymentStateLabel = (appointment) => {
        const billingItem = getBillingItem(appointment);
        const paymentState = billingItem?.paymentState;

        if (paymentState === 'PAID_ONLINE' || appointment?.paymentStatus === 'VERIFIED') {
            return 'Paid online';
        }
        if (paymentState === 'PAID_OFFLINE' || appointment?.paymentStatus === 'OFFLINE_RECORDED') {
            return 'Paid offline';
        }
        if (paymentState === 'PENDING_VERIFICATION' || ['CREATED', 'PENDING'].includes(billingItem?.onlinePaymentStatus)) {
            return 'Online payment pending';
        }
        if (paymentState === 'PAYMENT_NOT_CONFIRMED' || ['FAILED', 'REVERSED'].includes(appointment?.paymentStatus)) {
            return 'Payment not confirmed';
        }
        return 'Payment due';
    };

    const renderBillingChip = (appointment) => {
        if (appointment?.billingState !== 'ONI_BILLING' || !appointment?.paymentType) return null;
        const statusLabel = getPaymentStateLabel(appointment);
        const paymentTimingLabel = appointment.paymentType === 'PREPAID' ? 'Pay before' : 'Pay later';

        return (
            <Tooltip title={`${paymentTimingLabel}: ${statusLabel}`}>
                <Chip
                    label={`${paymentTimingLabel} · ${statusLabel}`}
                    size="small"
                    sx={{
                        height: '16px',
                        fontSize: '8px',
                        backgroundColor: appointment.paymentType === 'PREPAID' ? '#E3F2FD' : '#FFF3E0',
                    }}
                />
            </Tooltip>
        );
    };

    const renderCollectPayment = (appointment) => {
        const billingItem = getBillingItem(appointment);
        if (!canRecordOfflinePayment || !billingItem?.canConfirmOffline) return null;

        return (
            <Button
                size="small"
                variant="contained"
                onClick={(event) => {
                    event.stopPropagation();
                    setSelectedBill(billingItem);
                    setOfflineMethod(billingItem.offlineMethods?.[0] || "");
                }}
                sx={{
                    minWidth: 'auto',
                    minHeight: 28,
                    px: 1,
                    py: 0,
                    fontSize: '11px',
                    whiteSpace: 'nowrap',
                    textTransform: 'none',
                }}
            >
                Collect payment
            </Button>
        );
    };
    const AppointmentList = ({ appointments, queueCode }) => (
        <Grid item md={12} sx={{ p: 1 }}>
            {appointments.length == 0 && <Typography sx={{ fontSize: '12px' }} >No Records</Typography>}
            {appointments.map((appointment, index) => (
                <Grid
                    key={appointment.id || index}
                    item
                    md={12}
                    sx={{
                        mt: 1,
                        backgroundColor: '#F2F2F2',
                        borderRadius: '6px',
                        width: '100%',
                        minHeight: '45px',
                        p: 1,
                        cursor: 'pointer',
                    }}
                    alignContent={'center'}
                    onClick={() => {
                        if (!hasPermission('CONSULTATION_VIEW')) {
                            showToast("You don't have permission", "info")
                            return
                        }
                        handleClickPatient(appointment)
                    }}
                >
                    <Grid container spacing={0.4} alignItems="center">
                        <Grid size={{ xs: 7, sm: 12, xl: 7, lg: 7, md: 7 }} alignContent={'center'}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                <Tooltip title={`${appointment.patientFirstName} ${appointment.patientLastName}`}>
                                    <Typography
                                        fontSize={{ xs: '11px', sm: '11px', xl: "10px", lg: '12px', md: '12px' }}
                                        noWrap
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {appointment.patientFirstName} {appointment.patientLastName}
                                    </Typography>
                                </Tooltip>
                                {renderPriorityChip(appointment)}
                                {renderCheckedInChip(appointment)}
                                {renderBillingChip(appointment)}
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 2, sm: 4, xl: 2, lg: 2, md: 2 }} alignContent={'center'} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                            {appointment.patientRiskLevel ? (
                                <Typography
                                    sx={{
                                        backgroundColor: appointment.patientRiskLevel == "HIGH" ? 'rgba(255, 127, 122, 1)' : appointment.patientRiskLevel == "MEDIUM" ? 'rgba(230, 236, 250, 1)' : 'rgba(224, 239, 253, 1)',
                                        borderRadius: '5px',
                                        width: { xs: '45px', sm: '45px', xl: '49px', lg: '55px', md: '55px' },
                                        height: '18px',
                                        textAlign: 'center',
                                        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    fontSize={{ xs: '9px', sm: '9px', xl: "9px", lg: '11px', md: '10px' }}
                                >
                                    {appointment.patientRiskLevel}
                                </Typography>
                            ) : null}
                        </Grid>
                        <Grid size={{ xs: 2, sm: 4, xl: 3, lg: 3, md: 3 }} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.25, }}>
                            <Typography
                                sx={{
                                    minWidth: '49px',
                                    textAlign: 'center',
                                }}
                                fontSize={{ xs: '10px', sm: '10px', xl: "10px", lg: '10px', md: '10px' }}
                            >
                                {appointment.patientType === "GYNECOLOGY" ? '-' : formatWeeks(appointment.patientCurrentWeek)}
                            </Typography>
                        </Grid>
                        {queueCode === 'PAYMENT' && (
                            <Grid size={{ xs: 12, sm: 12, xl: 12, lg: 12, md: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }} onClick={(event) => event.stopPropagation()}>
                                {renderCollectPayment(appointment)}
                            </Grid>
                        )}
                        {/* <Grid size={{ xs: 4, sm: 4, xl: 4, lg: 4, md: 4 }} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
                            {hasMenuActions(appointment, queueCode) && (
                                <IconButton
                                    size="small"
                                    sx={{ p: 0 }}
                                    onClick={(event) => handleMenuOpen(event, { appointment, queueCode })}
                                    disabled={isMutatingQueue}
                                >
                                    <MoreVertIcon sx={{ fontSize: '16px' }} />
                                </IconButton>
                            )}
                        </Grid> */}
                    </Grid>
                </Grid>
            ))}
        </Grid>
    );

    const handleChangeDate = (date) => {
        const formattedDate = moment(date).format('YYYY-MM-DD');
        setSelectedDate(formattedDate);
        store.dispatch(slotActions.getSlotsRequestAction(formattedDate))
        trackEvent("dashboard_date_changed", {
            selected_date: formattedDate,
        });
    }

    const handleClickPatient = (appointment) => {
        trackEvent("patient_record_opened", {
            consultation_id:
                appointment.currentConsultationId ||
                appointment.lastConsultationId ||
                null,
            patient_type: appointment.patientType,
            appointment_status: appointment.status,
        });
        // last consultation id not present , current consultaiton id not present and first appointment
        if (!appointment.lastConsultationId && !appointment.currentConsultationId && appointment.isFirstTimeConsultation) {
            showToast("You have to start the consultation", "success");
        }
        // last consultation id present but current consultaiton id not present 
        if (appointment.lastConsultationId && !appointment.currentConsultationId) {
            // summary 
            screenBasisPermission(appointment.lastConsultationId, appointment.patientId, "view", hasPermission, navigate)
        }
        // Note -do not disable the top navigation 
        // last consultation id present , current consultaiton id present and second or repeat appointment 
        if (appointment.lastConsultationId && appointment.currentConsultationId) {
            screenBasisPermission(appointment.currentConsultationId, appointment.patientId, "view", hasPermission, navigate)
        }

        //  last consultaiton not present , current present
        if (!appointment.lastConsultationId && appointment.currentConsultationId) {
            screenBasisPermission(appointment.currentConsultationId, appointment.patientId, "view", hasPermission, navigate)
        }
    }

    useEffect(() => {
        const el = containerRef.current;
        if (el) {
            setIsOverflowing(el.scrollHeight > el.clientHeight);
        }
    }, [activeSlots]);

    // Auto-load next page if not overflowing
    useEffect(() => {
        if (!loading && hasNext && !isOverflowing && activeSlots.length > 0) {
            dispatch(slotActions.getSlotsRequestAction(selectedDate, page + 1));
        }
    }, [isOverflowing, hasNext, loading, activeSlots.length]);

    const formatText = (text) => {
        return text.toLowerCase()
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
    }

    const refreshDashboardData = () => {
        dispatch(slotActions.getSlotsRequestAction(selectedDate));
        dispatch(queueActions.getQueueRequestAction(selectedDate));
    };

    const getPaymentAppointments = () => {
        const appointmentsById = new Map();
        activeSlots.forEach((slot) => {
            slot.appointments?.forEach((appointment) => {
                appointmentsById.set(appointment.id, appointment);
            });
        });
        activeQueues.forEach((queue) => {
            queue.appointments?.forEach((appointment) => {
                appointmentsById.set(appointment.id, appointment);
            });
        });
        return Array.from(appointmentsById.values()).filter(
            (appointment) =>
                appointment.billingState === 'ONI_BILLING' &&
                Boolean(appointment.paymentType),
        );
    };

    const isAppointmentCheckedIn = (appointment) => Boolean(appointment?.checkedInAt);

    const canCheckInAppointment = (appointment) =>
        appointment?.status === "SCHEDULED" && !isAppointmentCheckedIn(appointment);

    const canStartConsultation = (appointment) =>
        appointment?.status === "IN_PROGRESS" &&
        isAppointmentCheckedIn(appointment) &&
        !appointment?.currentConsultationId;

    const canResumeConsultation = (appointment) =>
        appointment?.status === "IN_PROGRESS" && Boolean(appointment?.currentConsultationId);

    const canCancelAppointment = (appointment) =>
        appointment &&
        !["CANCELLED", "COMPLETED", "NO_SHOW"].includes(appointment.status);

    const canShowMoveToTop = (appointment, queueCode) =>
        hasPermission('APPOINTMENT_EDIT') &&
        appointment &&
        (queueCode === 'CONSULTATION' || (!queueCode && isAppointmentCheckedIn(appointment)));

    const hasMenuActions = (appointment, queueCode) => {
        if (isFutureDate) return false;
        if (!hasPermission('APPOINTMENT_EDIT') || !appointment) return false;
        return (
            canCheckInAppointment(appointment) ||
            canShowMoveToTop(appointment, queueCode) ||
            canCancelAppointment(appointment)
        );
    };

    const getStartButtonLabel = (appointment) => {
        if (canResumeConsultation(appointment)) return "Resume";
        if (canStartConsultation(appointment)) return "Start";
        if (canCheckInAppointment(appointment)) return "Check in";
        return "Start";
    };

    const getStartButtonDisabled = (appointment) =>
        isFutureDate ||
        !appointment?.patientIsVerified ||
        !(canCheckInAppointment(appointment) || canStartConsultation(appointment) || canResumeConsultation(appointment));

    const handleMenuOpen = (event, context) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuAnchorPosition({
            top: Math.round(rect.bottom),
            left: Math.round(rect.right),
        });
        setActionMenuContext(context);
    };

    const handleMenuClose = () => {
        setMenuAnchorPosition(null);
        setActionMenuContext(null);
    };

    const handleCheckIn = async (appointmentId) => {
        let baseUrl =
            process.env.NODE_ENV === 'development'
                ? process.env.REACT_APP_DEVELOP
                : process.env.REACT_APP_PROD;
        try {
            setIsMutatingQueue(true);
            const response = await API.post(`${baseUrl}${END_POINTS.checkInAppointment(appointmentId)}`);
            if (!response?.success) {
                throw new Error(response?.message || "Check-in failed");
            }
            showToast("Patient checked in successfully", "success");
            trackEvent("patient_checked_in", {
                appointment_id: appointmentId,
            });
            refreshDashboardData();
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.response?.data?.errors?.error ||
                error?.message ||
                "Check-in failed";
            showToast(message, "error");
            trackEvent("patient_check_in_failed");
        } finally {
            setIsMutatingQueue(false);
            handleMenuClose();
        }
    };

    const handleMoveToTop = async (appointmentId) => {
        let baseUrl =
            process.env.NODE_ENV === 'development'
                ? process.env.REACT_APP_DEVELOP
                : process.env.REACT_APP_PROD;
        try {
            setIsMutatingQueue(true);
            const response = await API.patch(`${baseUrl}${END_POINTS.reorderAppointmentQueue(appointmentId)}`);
            if (!response?.success) {
                throw new Error(response?.message || "Unable to reorder queue");
            }
            showToast("Patient moved to top", "success");
            refreshDashboardData();
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.response?.data?.errors?.error ||
                error?.message ||
                "Unable to reorder queue";
            showToast(message, "error");
        } finally {
            setIsMutatingQueue(false);
            handleMenuClose();
        }
    };

    const handleCancelAppointment = async (appointment) => {
        const patientName = `${appointment?.patientFirstName || ''} ${appointment?.patientLastName || ''}`.trim();
        const confirmed = window.confirm(
            `Cancel appointment${patientName ? ` for ${patientName}` : ''}?`
        );
        if (!confirmed) {
            handleMenuClose();
            return;
        }

        let baseUrl =
            process.env.NODE_ENV === 'development'
                ? process.env.REACT_APP_DEVELOP
                : process.env.REACT_APP_PROD;
        try {
            setIsMutatingQueue(true);
            const response = await API.post(`${baseUrl}${END_POINTS.cancelAppointments}`, {
                appointmentIds: [appointment.id],
            });
            if (!response?.success) {
                throw new Error(response?.message || "Unable to cancel appointment");
            }
            showToast("Appointment cancelled", "success");
            trackEvent("appointment_cancelled");
            refreshDashboardData();
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.response?.data?.errors?.error ||
                error?.message ||
                "Unable to cancel appointment";
            showToast(message, "error");
        } finally {
            setIsMutatingQueue(false);
            handleMenuClose();
        }
    };

    const handleRecordOfflinePayment = async () => {
        if (!selectedBill || !offlineMethod || isRecordingPayment) return;

        setIsRecordingPayment(true);
        try {
            const response = await API.post(
                END_POINTS.recordOfflinePayment(selectedBill.billId),
                {
                    method: offlineMethod,
                    amountMinor: selectedBill.amountMinor,
                    idempotencyKey: getStableIdempotencyKey(
                        offlineKeyRef,
                        `${selectedBill.billId}:${offlineMethod}`,
                    ),
                },
            );

            if (!response?.success) {
                throw new Error(response?.message || 'Unable to record offline payment');
            }

            showToast('Offline payment recorded successfully', 'success');
            offlineKeyRef.current = null;
            setSelectedBill(null);
            setOfflineMethod('');
            await loadBillingItems();
            refreshDashboardData();
        } catch (error) {
            showToast(
                error?.response?.data?.message ||
                    error?.message ||
                    'Unable to record offline payment',
                'error',
            );
        } finally {
            setIsRecordingPayment(false);
        }
    };

    const renderPriorityChip = (appointment) => {
        if (appointment?.patientPriority !== "VIP") return null;
        return (
            // <Chip
            //     label="VIP"
            //     size="small"
            //     sx={{
            //         height: '18px',
            //         fontSize: '9px',
            //         fontWeight: 600,
            //         backgroundColor: '#D4F0F4',
            //         color: '#333',
            //     }}
            // />
            <Tooltip title="VIP">
                <Crown height={18} width={18} />
            </Tooltip>
        );
    };

    const renderAppointment = () => {
        return <Grid size={{ xs: 12, sm: 12, md: 12 }} sx={{ height: { xs: 'auto', sm: 'auto', md: '71vh', lg: "60vh", xl: '71vh', } }} px={{ xs: 1, sm: 2, md: 3 }} ref={containerRef}>
            {activeSlots.map((item) => {
                return <Grid container mt={1} key={item.id}>
                    <Grid size={{ xs: 12, sm: 12, md: 12 }} justifyItems={"flex-start"}>
                        <Typography fontSize={{ xs: '14px', sm: '16px', md: '18px' }}>
                            {(moment(item.startDateTime).format('hh:mm A'))}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12 }} >
                        <Grid container>
                            {item.appointments.map((appointment) => {
                                const color = appointment.patientRiskLevel == "HIGH" ? 'rgba(255, 127, 122, 1)' : appointment.patientRiskLevel == "MEDIUM" ? 'rgba(230, 236, 250, 1)' : 'rgba(224, 239, 253, 1)'

                                return <Grid size={{ xs: 12, sm: 12, md: 12 }} sx={{ mt: 1, cursor: 'pointer', backgroundColor: '#F2F2F2', borderRadius: '6px', width: { xs: '100%', sm: '100%', md: '100%' }, minHeight: '45px', height: { xs: 'auto', sm: 'auto', md: '45px' }, p: { xs: '6px 4px', md: 1 } }} alignContent={'center'} onClick={() => {
                                    if (!hasPermission('CONSULTATION_VIEW')) {
                                        showToast("You don't have permission", "info")
                                        return
                                    }
                                    handleClickPatient(appointment)
                                }}>
                                    {/* Mobile layout — single row */}
                                    <Box
                                        sx={{
                                            display: { xs: 'flex', md: 'none' },
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            p: 1,
                                        }}
                                    >
                                        {/* Left */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            {/* Name */}
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                <Typography
                                                    fontWeight={600}
                                                    noWrap
                                                    sx={{ maxWidth: "100%", fontSize: '13px' }}
                                                >
                                                    {appointment.patientFirstName} {appointment.patientLastName}
                                                </Typography>

                                                {renderPriorityChip(appointment)}
                                                {renderCheckedInChip(appointment)}
                                                {renderBillingChip(appointment)}
                                            </Box>

                                            {/* Second Row */}
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                mt={0.5}
                                                alignItems="center"
                                                flexWrap="wrap"
                                            >
                                                <Typography fontSize={10} color="text.secondary" sx={{ color: '#555', }}>
                                                    {appointment.patientType === "GYNECOLOGY" ? '-' : formatWeeks(appointment.patientCurrentWeek)}
                                                </Typography>

                                                <Typography fontSize={11} color="text.secondary" sx={{ color: '#444', }}>
                                                    {formatText(appointment.patientType)}
                                                </Typography>

                                                {appointment.patientRiskLevel && (
                                                    <Chip
                                                        size="small"
                                                        label={appointment.patientRiskLevel}
                                                        sx={{
                                                            bgcolor: color,
                                                            height: 22,
                                                            fontSize: 10,
                                                        }}
                                                    />
                                                )}
                                            </Stack>
                                        </Box>

                                        {/* Right */}
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            ml={1}
                                            flexWrap="wrap"
                                        >

                                            {!appointment.patientIsVerified ? (
                                                <Typography sx={{ cursor: isFutureDate ? 'not-allowed' : 'pointer', opacity: isFutureDate ? 0.6 : 1, backgroundColor: '#FFFF', borderRadius: '4px', minWidth: 'auto', textAlign: 'center', height: '28px', display: 'flex', alignItems: 'center', border: '1px solid #D3D3D3', justifyContent: 'center', px: 1, boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)', flexShrink: 0, whiteSpace: 'nowrap' }} fontSize="11px" onClick={(e) => {
                                                    if(isFutureDate) return;
                                                    handleVerifyPatient(e, appointment, item)
                                                }}>
                                                    Verify
                                                </Typography>
                                            ) : (
                                                <>
                                                <Tooltip title={getStartButtonTooltip(appointment)}>
                                                    <Typography sx={{ cursor: getStartButtonDisabled(appointment) ? 'not-allowed' : 'pointer', opacity: getStartButtonDisabled(appointment) ? 0.6 : 1, backgroundColor: '#FFFF', border: '1px solid #D3D3D3', borderRadius: '4px', minWidth: 'auto', textAlign: 'center', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1, boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)', flexShrink: 0, whiteSpace: 'nowrap' }} fontSize="11px" onClick={(e) =>
                                                        handleStartResume(e, appointment, item)
                                                    }>
                                                        {getStartButtonLabel(appointment)}
                                                    </Typography>
                                                </Tooltip>
                                                </>
                                            )}

                                            {renderCollectPayment(appointment)}

                                            {hasMenuActions(appointment) && (
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleMenuOpen(e, { appointment })}
                                                >
                                                    <MoreVertIcon />
                                                </IconButton>
                                            )}
                                        </Stack>
                                    </Box>

                                    {/* Desktop layout — prod spacing */}
                                    <Grid container spacing={1} sx={{ display: { xs: 'none', md: 'flex' } }}>
                                        <Grid size={{ xs: 6, sm: 3, xl: 3, lg: 3, md: 2 }} justifyItems={"flex-start"} alignSelf={"center"}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, maxWidth: '100%' }}>
                                                <Typography sx={{
                                                    height: '22px', alignContent: 'center', whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    maxWidth: '100%',
                                                }} fontSize={{ xs: '12px', sm: '12px', xl: "14px", lg: '14px', md: '12px' }}>
                                                    {appointment.patientFirstName} {appointment.patientLastName}
                                                </Typography>
                                                {renderPriorityChip(appointment)}
                                                {renderCheckedInChip(appointment)}
                                                {renderBillingChip(appointment)}
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 6, sm: 2, xl: 2, lg: 2, md: 2 }} justifyItems={'center'} alignSelf={"center"}>
                                            <Typography sx={{ height: '22px', alignContent: 'center' }} fontSize={{ xs: '11px', sm: '11px', xl: "14px", lg: '14px', md: '12px' }}>
                                                {formatText(appointment.patientType)}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 3, sm: 1.5, xl: 1.5, lg: 1.5, md: 1.7 }} justifyItems={"center"} alignSelf={"center"}>
                                            {appointment.patientRiskLevel && (
                                                <Typography sx={{ backgroundColor: color, borderRadius: '4px', height: '22px', alignContent: 'center', px: 1, textAlign: 'center' }} fontSize={{ xs: '10px', sm: '10px', xl: "13px", lg: '13px', md: '11px' }}>
                                                    {appointment.patientRiskLevel}
                                                </Typography>
                                            )}
                                        </Grid>
                                        <Grid size={{ xs: 3, sm: 1.5, xl: 1.5, lg: 1.5, md: 2 }} justifyItems={"center"} alignSelf={"center"}>
                                            <Typography sx={{ backgroundColor: '#D3D3D3', borderRadius: '4px', height: '24px', alignContent: 'center', px: 1, boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} fontSize={{ xs: '10px', sm: '10px', xl: "11px", lg: '11px', md: '10px' }}>
                                                {appointment.doctorFirstName}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 3, sm: 2, xl: 2, lg: 2, md: 2 }} justifyItems={"center"} alignSelf={"center"}>
                                            <Typography sx={{ height: '18px', alignContent: 'center', textAlign: 'center' }} fontSize={{ xs: '10px', sm: '10px', xl: "12px", lg: '12px', md: '11px' }}>
                                                {appointment.patientType === "GYNECOLOGY" ? '-' : formatWeeks(appointment.patientCurrentWeek)}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 3, sm: 2, xl: 2, lg: 2, md: 2 }} justifyItems={"center"} alignSelf={"center"}>
                                            <Box
                                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {!appointment.patientIsVerified ? (
                                                    <Typography sx={{ cursor: isFutureDate ? 'not-allowed' : 'pointer', opacity: isFutureDate ? 0.6 : 1, border: '1px solid #D3D3D3', backgroundColor: '#FFF', borderRadius: '4px', minWidth: '72px', height: '29px', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1, boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)', textAlign: 'center', whiteSpace: 'nowrap' }} fontSize={{ xs: '12px', sm: '12px', xl: "14px", lg: '13px', md: '12px' }} onClick={(e) => {
                                                        if(isFutureDate) return;
                                                        handleVerifyPatient(e, appointment, item)
                                                    }}>
                                                        Verify
                                                    </Typography>
                                                ) : (
                                                    <>
                                                    <Tooltip title={getStartButtonTooltip(appointment)}>
                                                        <Typography sx={{ cursor: getStartButtonDisabled(appointment) ? 'not-allowed' : 'pointer', opacity: getStartButtonDisabled(appointment) ? 0.6 : 1, border: '1px solid #D3D3D3', backgroundColor: '#FFF', borderRadius: '4px', minWidth: '72px', height: '29px', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1, boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)', textAlign: 'center', whiteSpace: 'nowrap' }} fontSize={{ xs: '12px', sm: '12px', xl: "14px", lg: '13px', md: '12px' }} onClick={(e) =>
                                                            handleStartResume(e, appointment, item)
                                                        }>
                                                            {getStartButtonLabel(appointment)}
                                                        </Typography>
                                                    </Tooltip>
                                                    </>
                                                )}
                                                {renderCollectPayment(appointment)}
                                                {hasMenuActions(appointment) && (
                                                    <IconButton
                                                        size="small"
                                                        sx={{ p: 0, width: 24, height: 24, ml: -0.5 }}
                                                        onClick={(event) => handleMenuOpen(event, { appointment })}
                                                        disabled={isMutatingQueue}
                                                    >
                                                        <MoreVertIcon sx={{ fontSize: '18px' }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            })}
                        </Grid>
                    </Grid>
                </Grid>
            })}
        </Grid>
    }

    const renderQueueActionMenu = () => {
        const appointment = actionMenuContext?.appointment;
        const queueCode = actionMenuContext?.queueCode;
        const showCheckIn = appointment && canCheckInAppointment(appointment) && hasPermission('APPOINTMENT_EDIT');
        const showMoveToTop = canShowMoveToTop(appointment, queueCode);
        const showCancel = appointment && canCancelAppointment(appointment) && hasPermission('APPOINTMENT_EDIT');

        return (
            <Menu
                anchorReference="anchorPosition"
                anchorPosition={menuAnchorPosition ?? undefined}
                open={Boolean(menuAnchorPosition)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: { minWidth: 150 },
                    },
                }}
            >
                {showCheckIn && (
                    <MenuItem disabled={isMutatingQueue} onClick={() => handleCheckIn(appointment.id)}>
                        Check in
                    </MenuItem>
                )}
                {showMoveToTop && (
                    <MenuItem disabled={isMutatingQueue} onClick={() => handleMoveToTop(appointment.id)}>
                        Move to top
                    </MenuItem>
                )}
                {showCancel && (
                    <MenuItem disabled={isMutatingQueue} onClick={() => handleCancelAppointment(appointment)}>
                        Cancel appointment
                    </MenuItem>
                )}
            </Menu>
        );
    };

    const handleStartResumeLogic = (appointment, item, autoJoinVideoCall = false) => {
        const navState = {
            appointmentId: appointment.id,
            appointmentMode: appointment.mode,
            autoJoinVideoCall,
            patientName: [appointment.patientFirstName, appointment.patientLastName].filter(Boolean).join(' ')
        };
        if (appointment?.id && appointment?.mode) {
            try {
                sessionStorage.setItem(`appointmentMode_${appointment.id}`, appointment.mode);
            } catch (e) {}
        }

        if (
            canStartConsultation(appointment)
        ) {
            pendingNavigationState.current = navState;
            trackEvent("consultation_started", {
                patient_type: appointment.patientType,
                first_consultation: appointment.isFirstTimeConsultation,
            });
            store.dispatch(
                consultationActions.createConsultationRequestAction(
                    appointment.patientId,
                    appointment.id,
                    item.hospitalId,
                )
            );
        }

        else if (
            appointment.status === "IN_PROGRESS" &&
            appointment.currentConsultationId
        ) {
            trackEvent("consultation_resumed", {
                patient_type: appointment.patientType,
            });
            const { patientId, value } = storedRoute ? JSON.parse(storedRoute) : {}
            if (appointment.isFirstTimeConsultation) {
                if (patientId == appointment?.patientId) {
                    const staticRoutes = ['l', 'e', 'p', 'summary']
                    if (staticRoutes.includes(value)) {
                        navigate(`/consultation/${appointment.currentConsultationId}/patient/${appointment.patientId}/${value}?mode=edit`, { state: navState })
                    }
                    else {
                        navigate(`/consultation/${appointment.currentConsultationId}/patient/${appointment.patientId}/A/${value}?mode=edit`, { state: navState })
                    }
                }
                else {
                    if (hasPermission('HISTORY_VIEW')) {
                        navigate(`/consultation/${appointment.currentConsultationId}/patient/${appointment.patientId}/A/history?mode=edit`, { state: navState })
                        return
                    }
                    if (hasPermission('VITALS_VIEW')) {
                        navigate(`/consultation/${appointment.currentConsultationId}/patient/${appointment.patientId}/A/vitals?mode=edit`, { state: navState })
                        return
                    }
                    if (hasPermission('OBSERVATIONS_VIEW')) {
                        navigate(`/consultation/${appointment.currentConsultationId}/patient/${appointment.patientId}/A/observation?mode=edit`, { state: navState })
                        return
                    }
                    if (hasPermission('TEST_VIEW')) {
                        navigate(`/consultation/${appointment.currentConsultationId}/patient/${appointment.patientId}/A/antenatal?mode=edit`, { state: navState })
                        return
                    }
                    if (hasPermission('TREATMENT_VIEW')) {
                        navigate(`/consultation/${appointment.currentConsultationId}/patient/${appointment.patientId}/A/treatment?mode=edit`, { state: navState })
                        return
                    }
                }
            }
            else {
                if (patientId == appointment?.patientId) {

                    const staticRoutes = ['l', 'e', 'p', 'summary']
                    if (staticRoutes.includes(value)) {
                        navigate(`/consultation/${appointment.currentConsultationId}/patient/${appointment.patientId}/${value}?mode=edit`, { state: navState })
                    }
                    else {
                        navigate(`/consultation/${appointment.currentConsultationId}/patient/${appointment.patientId}/A/${value}?mode=edit`, { state: navState })
                    }
                }
                else {
                    screenBasisPermission(appointment.currentConsultationId, appointment.patientId, "edit", hasPermission, navigate, navState)
                }
            }
        }
    };

    const sendOtp = async (phone, code) => {
        try {
            const signinResponse = await API.post(END_POINTS.sendOtpToPatient, {
                phone,
                countryCode: code,
                platform: "ONI_MOM_MOBILE"
            });
            if (signinResponse.success) {
                setOtpSent(true);
                showToast("OTP sent successfully", "success");
            }
            else {
                throw new Error(signinResponse.message)
            }
        } catch (err) {
            trackEvent("patient_otp_send_failed");
            showToast("Failed to send OTP", "error");
        }
    };

    const handleVerifyPatient = async (
        e,
        appointment,
        item
    ) => {
        e.stopPropagation();
        trackEvent("patient_verification_started", {
            patient_type: appointment.patientType,
        });
        setSelectedAppointment({
            appointment,
            item,
        });

        setPhoneNumber(
            appointment.patientPhoneNumber
        );

        setOtpModalOpen(true);

        try {
            await sendOtp(
                appointment.patientPhoneNumber,
                appointment.patientCountryCode
            );
        } catch (err) {
            showToast(
                "Unable to send OTP",
                "error"
            );
        }
    };

    const handleStartResume = async (e, appointment, item, autoJoinVideoCall = false) => {
        e.stopPropagation();

        if (isFutureDate) return;

        if (getStartButtonDisabled(appointment) && !autoJoinVideoCall) {
            if (!appointment.patientIsVerified) {
                showToast("Verify patient first", "info");
            }
            return;
        }

        if (canCheckInAppointment(appointment)) {
            if (!hasPermission("APPOINTMENT_EDIT")) {
                showToast("You don't have permission to check in", "info");
                return;
            }
            await handleCheckIn(appointment.id);
            return;
        }

        if (!hasPermission("CONSULTATION_CREATE")) {
            showToast("You don't have permission to start", "info");
            return;
        }

        if (!isAppointmentCheckedIn(appointment)) {
            showToast("Please check in the patient before starting consultation", "info");
            return;
        }

        let shouldAutoJoin = autoJoinVideoCall;
        if (!shouldAutoJoin && appointment.mode === 'VIDEO_CALL') {
            shouldAutoJoin = true;
        }

        handleStartResumeLogic(appointment, item, shouldAutoJoin);
    };

    const handleVerifyOtp = async () => {
        try {
            const response = await API.post(
                END_POINTS.verifyPatientOtp,
                {
                    phone: phoneNumber,
                    countryCode: selectedAppointment.appointment.patientCountryCode,
                    platform: "ONI_MOM_MOBILE",
                    otp
                }
            );
            if (!response?.success) {
                throw new Error(response.message)
            }
            if (response?.success) {
                onClosingVerifyPatientDialog()
                // check with shabeer how will do this 

                // if (response?.data?.isExistingPatient) {
                //     showToast(`Patient already exist with name ${response?.data?.firstName} ${response?.data?.lastName}`, 'info')
                // }
                showToast(
                    "Patient verified successfully",
                    "success"
                );

                dispatch(slotActions.getSlotsRequestAction(selectedDate));
            }
        } catch (error) {
            trackEvent("patient_verification_failed");
            showToast("Invalid OTP", "error");
        }
    };

    const handleResendOtp = async () => {
        if (!selectedAppointment) return;

        await sendOtp(
            phoneNumber,
            selectedAppointment.appointment.patientCountryCode
        );

        setResendTimer(30);
        setCanResend(false);
    };

    const updatePhoneNumber = async () => {
        try {
            const response = await API.patch(
                `/v1/patients/${selectedAppointment.appointment.patientId}/update-phone-number`,
                {
                    phone: updatedPhone,
                    countryCode:
                        selectedAppointment.appointment.patientCountryCode,
                }
            );

            if (!response.success) {
                throw new Error(response.message);
            }

            setPhoneNumber(response.data.phoneNumber);

            showToast(
                "Phone number updated successfully",
                "success"
            );
            trackEvent("patient_phone_updated");
            setIsEditingPhone(false);

            await sendOtp(
                response.data.phoneNumber,
                response.data.countryCode
            );

        } catch (error) {
            showToast(
                error?.message || "Unable to update phone number",
                "error"
            );
        }
    };

    const handleBookAppointment = (patientReference) => {
        setPatientReffered(patientReference)
        setShowDropdown((prev) => !prev);
    };

    const clearBookAppointment = () => {
        setPatientReffered({})
        setShowDropdown(false);
    };

    const onClosingVerifyPatientDialog = () => {
        setOtpModalOpen(false)
        setOtp('')
        setSelectedAppointment(null)
        setPhoneNumber('')
        setOtpSent(false)
        setResendTimer(30);
        setCanResend(false);
        setIsEditingPhone(false)
        setUpdatedPhone('')
    }

    return (
        <Grid container spacing={{ xs: 1, sm: 2, md: 2 }} sx={{ p: { xs: 1, sm: 2, md: 2 }, height: { xs: 'auto', sm: 'auto', xl: '100vh', lg: '100vh', md: '100%' }, }} >
            <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                <Grid container>
                    <Grid size={{ xs: 12, sm: 12, md: 12 }} justifyItems={'center'}>
                        <DateSlider handleChangeDate={handleChangeDate} />
                    </Grid>
                </Grid>
                <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} p={{ xs: 1, sm: 1, md: 2 }}>
                    {hasPermission("PATIENT_VIEW") && <Grid size={{ xs: 12, sm: 12, md: 6 }} >
                        <SearchModal handleBookAppointment={handleBookAppointment} />
                    </Grid>}
                    {hasPermission('APPOINTMENT_CREATE') && <Grid size={{ xs: 12, sm: 12, md: 5.5 }}>
                        <ModalComponent openBookAppointment={openBookAppointment} patientReffered={patientReffered} clearBookAppointment={clearBookAppointment} />
                    </Grid>}
                </Grid>
                {isFirstLoad ?
                    <CircularProgress />
                    : hasPermission("APPOINTMENT_VIEW") && (isOverflowing ?
                        <Grid sx={{ height: { xs: 'auto', sm: 'auto', md: '71vh', lg: "71vh", xl: '73vh', }, paddingBottom: '15px', display: 'flex', flexDirection: 'column', overflow: { xs: 'visible', sm: 'visible', md: 'hidden' } }}>
                            <Box sx={{
                                flex: 1, minHeight: 0, height: '100%', overflow: { xs: 'visible', sm: 'visible', md: 'hidden' },
                                '& .custom-scrollbars__container': {
                                    height: '100%'
                                },
                                '& .custom-scrollbars__content': {
                                    height: '100%',
                                    maxHeight: '100%'
                                }
                            }}>
                                <Scrollbar padding="0 0rem" gridTemplate='auto / 1fr 20px' scrollerPadding="0rem" onReachBottom={() => {
                                    if (!loading && hasNext) {
                                        dispatch(slotActions.getSlotsRequestAction(selectedDate, page + 1));
                                    }
                                }}>
                                    {renderAppointment()}
                                </Scrollbar>
                            </Box>
                        </Grid>
                        :
                        <Grid sx={{ height: { xs: 'auto', sm: 'auto', md: '71vh', lg: "71vh", xl: '73vh', }, paddingBottom: '15px', display: 'flex', flexDirection: 'column', overflow: { xs: 'visible', sm: 'visible', md: 'hidden' } }}>
                            {renderAppointment()}
                        </Grid>)
                }
            </Grid>

            {hasPermission("QUEUE_VIEW") && <Grid size={{ xs: 12, sm: 12, md: 6 }} sx={{ borderRadius: 4, height: { xs: 'auto', sm: 'auto', md: 'auto', lg: "88vh", xl: '90vh' }, display: 'flex', flexDirection: 'column' }}>
                <Grid container mt={1} spacing={2} p={{ xs: 1, sm: 1, md: 2 }}
                    sx={{
                        flexWrap: 'wrap',
                        overflowY: 'auto',
                    }}>
                    {isFirstLoad ?
                        <CircularProgress />
                        :
                        activeQueues.map((queue) => {
                            const appointments = queue.code === 'PAYMENT'
                                ? getPaymentAppointments()
                                : queue.appointments;
                            const shouldScroll = appointments.length > 3;
                            return <Grid  key={queue.id} size={{ xs: 12, sm: 6, xl: 4, md: 6, lg: 6 }} sx={{ height: '251px', borderRadius: 4, border: '1px solid #D3D3D3', display: 'flex', flexDirection: 'column' }}>
                                <Typography fontSize={"14px"} fontWeight={"bold"} mt={'5px'} sx={{ textAlign: { xs: 'center', sm: 'center', md: 'left' }, pl: { md: 1 }, flexShrink: 0 }}>
                                    {queue.code === 'PAYMENT' ? 'Billing status' : queue.name}
                                </Typography>
                                <Box sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    '& .custom-scrollbars__container': {
                                        height: '100%',
                                    },
                                    '& .custom-scrollbars__content': {
                                        height: '100%',
                                        maxHeight: '100%',
                                    },
                                    '& .custom-scrollbars__track-and-thumb': {
                                        height: '100%',
                                    },
                                    }}>
                                    {shouldScroll ? (
                                        <Scrollbar padding="0 0rem" gridTemplate='auto / 1fr 19px' scrollerPadding="0rem" scrollerWidth="12px">
                                            <AppointmentList appointments={appointments} queueCode={queue.code} />
                                        </Scrollbar>
                                    ) : (
                                        <AppointmentList appointments={appointments} queueCode={queue.code} />
                                    )}
                                </Box>
                            </Grid>
                        })}
                </Grid>
            </Grid>}
            {renderQueueActionMenu()}
            <Dialog
                open={Boolean(selectedBill)}
                onClose={() => {
                    if (!isRecordingPayment) {
                        setSelectedBill(null);
                        setOfflineMethod('');
                    }
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Collect payment offline</DialogTitle>
                <DialogContent>
                    <Typography mb={2}>
                        Confirm only after receiving the full{' '}
                        {formatBillingAmount(
                            selectedBill?.amountMinor || 0,
                            selectedBill?.currency || 'INR',
                        )}.
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {(selectedBill?.offlineMethods || [])
                            .filter((method) => offlineMethodLabels[method])
                            .map((method) => (
                                <Button
                                    key={method}
                                    variant={offlineMethod === method ? 'contained' : 'outlined'}
                                    onClick={() => setOfflineMethod(method)}
                                >
                                    {offlineMethodLabels[method]}
                                </Button>
                            ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setSelectedBill(null);
                            setOfflineMethod('');
                        }}
                        disabled={isRecordingPayment}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleRecordOfflinePayment}
                        disabled={!offlineMethod || isRecordingPayment}
                    >
                        {isRecordingPayment ? 'Saving…' : 'Confirm funds collected'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={otpModalOpen}
                onClose={() => {
                    onClosingVerifyPatientDialog()
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "15px",
                    },
                }}
            >
                <DialogContent sx={{ p: 4 }}>
                    <Box textAlign="center">
                        <TaskAltIcon sx={{ fontSize: '52px' }} />
                        <Typography
                            variant="h5"
                            fontWeight={700}
                            gutterBottom
                        >
                            Patient Verification
                        </Typography>
                        <Typography
                            color="text.secondary"
                            mb={1}
                        >
                            OTP has been sent successfully to
                        </Typography>
                        <Box sx={{ flexDirection: 'row', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Typography
                                variant="h6"
                                fontWeight={700}
                                color="primary"
                            >
                                {selectedAppointment?.appointment?.patientCountryCode}
                                {" "}
                                {phoneNumber}
                            </Typography>

                            <EditIcon style={{ cursor: 'pointer', width: 22, height: 22, marginLeft: 10 }} onClick={() => {
                                setUpdatedPhone(phoneNumber);
                                setIsEditingPhone(true)
                            }} />
                        </Box>

                        {isEditingPhone && (
                            <Box mt={2}>
                                <OtpInput
                                    value={updatedPhone}
                                    valueLength={10}
                                    onChange={(data) => {
                                        setUpdatedPhone(
                                            data.replace(/\D/g, "")
                                        )
                                    }}
                                    screen="dashboard"
                                />

                                <Button
                                    sx={{ mt: 2 }}
                                    variant="contained"
                                    onClick={updatePhoneNumber}
                                    disabled={updatedPhone.length !== 10}
                                >
                                    Update & Send OTP
                                </Button>
                            </Box>
                        )}
                        <Box
                            sx={{
                                mt: 3,
                                p: 2,
                                borderRadius: 2,
                                background: "#F5F8FF",
                                textAlign: "left"
                            }}
                        >
                            <Typography fontSize={14}>
                                Please ask the patient to check their
                                mobile phone and share the OTP.
                            </Typography>
                        </Box>

                        {(
                            <>
                                <Divider sx={{ mt: 3, backgroundColor: 'transparent' }} />
                                <OtpInput
                                    value={otp}
                                    valueLength={6}
                                    onChange={(e) => {
                                        const value = e.replace(/\D/g, "");
                                        if (value.length <= 6) {
                                            setOtp(value);
                                        }
                                    }}
                                    screen="dashboard"
                                />
                                <Box
                                    mt={2}
                                    display="flex"
                                    justifyContent="center"
                                    gap={1}
                                >
                                    <Typography color="text.secondary">
                                        Didn't receive OTP?
                                    </Typography>
                                    {canResend ? (
                                        <Typography
                                            sx={{
                                                color: "#1976d2",
                                                cursor: "pointer",
                                                fontWeight: 600
                                            }}
                                            onClick={handleResendOtp}
                                        >
                                            Resend OTP
                                        </Typography>
                                    ) : (
                                        <Typography color="text.secondary">
                                            ({resendTimer}s)
                                        </Typography>
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        pb: 3,
                        justifyContent: "space-between"
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={() =>
                            onClosingVerifyPatientDialog()
                        }
                    >
                        Cancel
                    </Button>
                    {otpSent && <Button
                        variant="contained"
                        onClick={handleVerifyOtp}
                        disabled={otp.length < 6}
                    >
                        Verify otp
                    </Button>}
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default DashBoard;
